/**
 * Export the motion timeline as a video file. Shows, before anything runs,
 * exactly what this browser will produce (container, codec, size) and why
 * when that differs from what was asked for.
 */

import { useEffect, useId, useMemo, useState } from 'react';
import type { ProjectPage, VideoContainer, VideoExportSettings, VideoFps } from '@canvas/shared-types';
import {
  applySizePreset,
  ARTBOARD_PRESET_ID,
  sizePresetIdFor,
  VIDEO_FPS_OPTIONS,
  VIDEO_QUALITY_OPTIONS,
  VIDEO_SIZE_PRESETS,
  withDerivedExportFields,
  type VideoQuality,
} from '@/data/videoExportPresets';
import { useVideoExport } from '@/hooks/useVideoExport';
import { IN_MEMORY_EXPORT_LIMIT_BYTES, saveFilePicker } from '@/lib/video/browserSupport';
import { planEncoder, type EncoderPlan } from '@/lib/video/encoderCapabilities';
import { describeExportError, isAbortError } from '@/lib/video/exportErrors';
import { fetchExportQuota, SUBSCRIPTIONS_ENABLED } from '@/lib/exportLimits';
import { exportFrameCount } from '@/lib/video/frameTiming';
import { formatTimecode } from '@/lib/video/sceneTimeline';
import { clipAudible, timelineClips } from '@/lib/video/videoClip';
import { useVideoStore } from '@/stores/videoStore';
import ScenePreviewOverlay from './ScenePreviewOverlay';

interface VideoExportDialogProps {
  open: boolean;
  projectId: string | null;
  projectName: string;
  /** Flush the live canvas and return up-to-date pages. */
  getPages: () => ProjectPage[];
  onClose: () => void;
}

function describePlan(plan: EncoderPlan): string {
  const codec =
    plan.backend === 'mediarecorder'
      ? 'real-time recording'
      : plan.container === 'mp4'
        ? 'H.264'
        : 'VP9';
  return `${plan.container.toUpperCase()} (${codec}) · ${plan.width}×${plan.height} · ${plan.fps} fps`;
}

function megabytes(bitrate: number, durationMs: number): string {
  const mb = (bitrate * (durationMs / 1000)) / 8 / 1_000_000;
  return mb < 1 ? `${Math.max(0.1, Math.round(mb * 10) / 10)} MB` : `${Math.round(mb)} MB`;
}

export default function VideoExportDialog({
  open,
  projectId,
  projectName,
  getPages,
  onClose,
}: VideoExportDialogProps) {
  const titleId = useId();
  const video = useVideoStore((s) => s.video);
  const setExportSettings = useVideoStore((s) => s.setExportSettings);
  const { progress, result, running, saving, saved, start, cancel, reset, download, saveToProject } =
    useVideoExport();

  const [pages, setPages] = useState<ProjectPage[]>([]);
  const [settings, setSettings] = useState<VideoExportSettings | null>(null);
  const [plan, setPlan] = useState<EncoderPlan | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saveToDisk, setSaveToDisk] = useState(false);
  const [quotaNote, setQuotaNote] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const hasClipSound = useMemo(
    () => (video ? timelineClips(pages, video).some((c) => clipAudible(c.clip)) : false),
    [pages, video]
  );
  const hasSound = Boolean(video?.audio)
    || hasClipSound
    || Boolean(video?.v === 2 && video.clips?.some((clip) => clip.kind === 'audio' || (clip.kind === 'video' && clip.video && !clip.video.muted)));

  // Snapshot pages and settings once per open; the modal blocks edits meanwhile.
  useEffect(() => {
    if (!open) {
      reset();
      setPreviewOpen(false);
      return;
    }
    const current = useVideoStore.getState().video;
    setPages(getPages());
    setSettings(current ? withDerivedExportFields(current.exportSettings) : null);
    setSaveError(null);
    setLinkCopied(false);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // TODO(subscription): tell the user how many exports they have left once Pro ships.
  useEffect(() => {
    if (!open || !SUBSCRIPTIONS_ENABLED) {
      setQuotaNote(null);
      return;
    }
    let stale = false;
    void fetchExportQuota().then((q) => {
      if (stale || q.unlimited) return;
      setQuotaNote(`${q.remaining} export${q.remaining === 1 ? '' : 's'} left this month`);
    });
    return () => {
      stale = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !settings) return;
    let stale = false;
    setPlan(null);
    setPlanError(null);
    planEncoder(settings)
      .then((p) => !stale && setPlan(p))
      .catch((err) => !stale && setPlanError(describeExportError(err)));
    return () => {
      stale = true;
    };
  }, [open, settings]);

  useEffect(() => {
    if (!open || previewOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !running) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, previewOpen, running, onClose]);

  const artboard = useMemo(() => {
    const first = pages[0]?.canvasData;
    return { width: first?.width || 1080, height: first?.height || 1920 };
  }, [pages]);

  if (!open || !video || !settings) return null;

  const durationMs = video.totalDurationMs;
  const presetId = sizePresetIdFor(settings);
  const update = (next: VideoExportSettings) => setSettings(withDerivedExportFields(next));
  const busy = running || progress.stage === 'prepare';
  const failed = progress.stage === 'failed';
  const showProgress = running || failed;

  const handleClose = () => {
    if (running) cancel();
    onClose();
  };

  const estimatedBytes =
    ((plan?.videoBitrate ?? 0) + (settings.includeAudio && hasSound ? settings.audioBitrate : 0)) *
    (durationMs / 1000) /
    8;
  const picker = saveFilePicker();
  const canStreamToDisk = plan?.backend === 'webcodecs' && plan.container === 'mp4' && picker !== null;
  const mustStream = canStreamToDisk && estimatedBytes >= IN_MEMORY_EXPORT_LIMIT_BYTES;

  const handleExport = async () => {
    if (!plan) return;
    let fileHandle: FileSystemFileHandle | undefined;
    if (picker && canStreamToDisk && (mustStream || saveToDisk)) {
      try {
        fileHandle = await picker({
          suggestedName: `${projectName || 'craftara-video'}.mp4`,
          types: [{ description: 'MP4 video', accept: { 'video/mp4': ['.mp4'] } }],
        });
      } catch (err) {
        if (isAbortError(err)) return;
        setPlanError('Couldn’t open a file to save into. Try Download after exporting instead.');
        return;
      }
    }
    setExportSettings(settings);
    void start({ pages: getPages(), video, settings, plan, fileHandle });
  };

  const handleSave = async () => {
    if (!projectId) return;
    setSaveError(await saveToProject(projectId, settings.platformId));
  };

  return (
    <div className="ps-modal-backdrop" role="presentation" onClick={running ? undefined : handleClose}>
      <div
        className="ps-modal vt-export-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ps-modal-header">
          <h2 id={titleId}>Export video</h2>
          <button type="button" className="ps-menu-link" onClick={handleClose}>
            Close
          </button>
        </header>

        <div className="ps-modal-body">
          <p className="ps-hint">
            {video.v === 2
              ? `${video.clips?.length ?? 0} ${(video.clips?.length ?? 0) === 1 ? 'clip' : 'clips'}`
              : `${video.scenes.length} ${video.scenes.length === 1 ? 'scene' : 'scenes'}`}{' '}
            ·{' '}
            {formatTimecode(durationMs)} · {exportFrameCount(durationMs, settings.fps)} frames
          </p>

          {result ? (
            <div className="vt-export-result">
              <video className="vt-export-video" src={result.url} controls playsInline />
              <p className="ps-hint">
                {result.mimeType.includes('mp4') ? 'MP4' : 'WebM'} · {result.width}×{result.height} ·{' '}
                {(result.blob.size / 1_000_000).toFixed(1)} MB
              </p>
              {result.note && <p className="ps-hint vt-export-note">{result.note}</p>}
              {saved && <p className="ps-hint">Saved to this project’s reels.</p>}
              {saveError && <p className="ps-error">{saveError}</p>}
            </div>
          ) : (
            <fieldset className="vt-export-fields" disabled={busy}>
              <label className="ps-field">
                Size
                <select
                  value={presetId}
                  onChange={(e) => update(applySizePreset(settings, e.target.value, artboard))}
                >
                  <option value={ARTBOARD_PRESET_ID}>
                    Match artboard · {applySizePreset(settings, ARTBOARD_PRESET_ID, artboard).width}×
                    {applySizePreset(settings, ARTBOARD_PRESET_ID, artboard).height}
                  </option>
                  {VIDEO_SIZE_PRESETS.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="ps-field">
                Frame rate
                <select
                  value={settings.fps}
                  onChange={(e) => update({ ...settings, fps: Number(e.target.value) as VideoFps })}
                >
                  {VIDEO_FPS_OPTIONS.map((fps) => (
                    <option key={fps} value={fps}>
                      {fps} fps
                    </option>
                  ))}
                </select>
              </label>
              <label className="ps-field">
                Quality
                <select
                  value={settings.quality}
                  onChange={(e) => update({ ...settings, quality: e.target.value as VideoQuality })}
                >
                  {VIDEO_QUALITY_OPTIONS.map((q) => (
                    <option key={q.id} value={q.id}>
                      {q.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="ps-field">
                Format
                <select
                  value={settings.container}
                  onChange={(e) => update({ ...settings, container: e.target.value as VideoContainer })}
                >
                  <option value="mp4">MP4 (plays everywhere)</option>
                  <option value="webm">WebM</option>
                </select>
              </label>

              {hasSound && (
                <label className="ps-field ps-field-checkbox">
                  <span className="ps-checkbox-row">
                    <input
                      type="checkbox"
                      checked={settings.includeAudio}
                      onChange={(e) => update({ ...settings, includeAudio: e.target.checked })}
                    />
                    {video.audio
                      ? `Include ${hasClipSound ? 'music and clip sound' : 'music'} · ${video.audio.name}`
                      : 'Include clip sound'}
                  </span>
                </label>
              )}

              {plan ? (
                <>
                  <p className="ps-hint">
                    Will export <strong>{describePlan(plan)}</strong>
                    {!hasSound || !settings.includeAudio
                      ? ' without sound'
                      : plan.backend === 'mediarecorder' || plan.audioCodecString
                        ? video.audio
                          ? ' with music'
                          : ' with sound'
                        : ' without sound (this browser can’t encode audio for this format)'}
                    , about {megabytes(plan.videoBitrate + (settings.includeAudio && hasSound ? settings.audioBitrate : 0), durationMs)}.
                  </p>
                  {plan.reason && <p className="ps-hint vt-export-note">{plan.reason}</p>}
                  {quotaNote && <p className="ps-hint">{quotaNote}</p>}
                  {plan.backend === 'mediarecorder' && (
                    <p className="ps-hint">Keep this tab in front while it records.</p>
                  )}
                  {estimatedBytes >= IN_MEMORY_EXPORT_LIMIT_BYTES && !canStreamToDisk && (
                    <p className="ps-hint vt-export-note">
                      This export is about {Math.round(estimatedBytes / 1_000_000)} MB and stays in
                      memory until it finishes. Shorten it if the tab slows down. Chrome can save a
                      large MP4 straight to disk.
                    </p>
                  )}
                  {canStreamToDisk && (mustStream || estimatedBytes >= 80_000_000) && (
                    <label className="ps-field ps-field-checkbox">
                      <span className="ps-checkbox-row">
                        <input
                          type="checkbox"
                          checked={mustStream || saveToDisk}
                          disabled={mustStream}
                          onChange={(e) => setSaveToDisk(e.target.checked)}
                        />
                        {mustStream
                          ? 'Save straight to disk — this file is too large to hold in memory'
                          : 'Save straight to disk (uses less memory)'}
                      </span>
                    </label>
                  )}
                </>
              ) : planError ? (
                <p className="ps-error">{planError}</p>
              ) : (
                <p className="ps-hint">Checking what this browser can encode…</p>
              )}
            </fieldset>
          )}

          {showProgress && (
            <div className="vt-export-progress" role="status" aria-live="polite">
              <div className="vt-export-bar">
                <div className="vt-export-bar-fill" style={{ width: `${progress.percent ?? 0}%` }} />
              </div>
              <p className="ps-hint">
                {progress.label}
                {progress.etaHint ? ` · ${progress.etaHint}` : ''}
              </p>
              {failed && progress.error && <p className="ps-error">{progress.error}</p>}
            </div>
          )}
        </div>

        <footer className="ps-modal-footer">
          {result ? (
            <>
              <button type="button" className="ps-btn ghost" onClick={reset}>
                Export again
              </button>
              {projectId && (
                <button
                  type="button"
                  className="ps-btn ghost"
                  disabled={saving || Boolean(saved)}
                  onClick={() => void handleSave()}
                >
                  {saving ? 'Saving…' : saved ? 'Saved' : 'Save to project'}
                </button>
              )}
              {saved?.videoUrl && (
                <button
                  type="button"
                  className="ps-btn ghost"
                  onClick={() => {
                    void navigator.clipboard?.writeText(saved.videoUrl).then(() => setLinkCopied(true));
                  }}
                >
                  {linkCopied ? 'Link copied' : 'Copy link'}
                </button>
              )}
              <button type="button" className="ps-btn primary" onClick={() => download(projectName)}>
                Download
              </button>
            </>
          ) : running ? (
            <button type="button" className="ps-btn ghost" onClick={cancel}>
              Cancel export
            </button>
          ) : (
            <>
              <button type="button" className="ps-btn ghost" onClick={() => setPreviewOpen(true)}>
                Preview
              </button>
              <button type="button" className="ps-btn primary" disabled={!plan} onClick={() => void handleExport()}>
                Export {plan ? plan.container.toUpperCase() : 'video'}
              </button>
            </>
          )}
        </footer>
      </div>

      <ScenePreviewOverlay
        open={previewOpen}
        pages={pages}
        video={video}
        size={{ width: plan?.width ?? settings.width, height: plan?.height ?? settings.height }}
        onClose={() => setPreviewOpen(false)}
      />
    </div>
  );
}
